import { TableERP } from "@finanedi.es/storybook"
import { useQuery } from "@tanstack/react-query"
import {
  getResumenImpuestos111,
  getResumenImpuestos115,
  getResumenImpuestos303,
  getResumenImpuestos349,
  getResumenImpuestos390,
} from "api/AsientoContable.service"
import { GetModelo111ResumenImpuestosDtoProps } from "api/DTOs/GetModelo111ResumenImpuestosDto"
import { GetModelo115ResumenImpuestosDtoProps } from "api/DTOs/GetModelo115ResumenImpuestosDto"
import { GetModelo303ResumenImpuestosDtoProps } from "api/DTOs/GetModelo303ResumenImpuestosDto"
import { GetModelo349ResumenImpuestosDtoProps } from "api/DTOs/GetModelo349ResumenImpuestosDto"
import { GetModelo390ResumenImpuestosDtoProps } from "api/DTOs/GetModelo390ResumenImpuestosDto"
import { MODELO_IMPUESTO, ModeloImpuestoType } from "api/Types/ModeloImpuesto"
import { Dropdown, DropdownChangeParams } from "primereact/dropdown"
import { useTranslation } from "react-i18next"
import {
  dateRangesList,
  RESUMEN_IMPUESTOS_COMPRAS_COLUMNS,
  RESUMEN_IMPUESTOS_VENTAS_COLUMNS,
} from "./constants"

type ModeloImpuestoDtoMap = {
  [MODELO_IMPUESTO.MODELO_111]: GetModelo111ResumenImpuestosDtoProps
  [MODELO_IMPUESTO.MODELO_115]: GetModelo115ResumenImpuestosDtoProps
  [MODELO_IMPUESTO.MODELO_303]: GetModelo303ResumenImpuestosDtoProps
  [MODELO_IMPUESTO.MODELO_349]: GetModelo349ResumenImpuestosDtoProps
  [MODELO_IMPUESTO.MODELO_390]: GetModelo390ResumenImpuestosDtoProps
}

type ResumenImpuestosDtoProps<
  T extends ModeloImpuestoType = ModeloImpuestoType
> = T extends keyof ModeloImpuestoDtoMap
  ? {
      elements: ModeloImpuestoDtoMap[T]
      tipo: T
    }
  : never

interface ResumenImpuestosProps {
  selectedDateRange: {
    min: string
    max: string
  }
  selectedYear: string
  handleRangeDateSelection: (e: DropdownChangeParams) => void
  detailModel: ModeloImpuestoType
}
export const ResumenImpuesto = ({
  selectedDateRange,
  selectedYear,
  handleRangeDateSelection,
  detailModel,
}: ResumenImpuestosProps) => {
  const { t } = useTranslation()

  const { data, isLoading } = useQuery<ResumenImpuestosDtoProps>({
    queryKey: [
      "resumenImpuestoCompras",
      detailModel,
      selectedDateRange.min,
      selectedDateRange.max,
    ],
    queryFn: async () => {
      const queryParams = {
        FechaLowerLimit: selectedDateRange.min,
        FechaUpperLimit: selectedDateRange.max,
      }
      switch (detailModel) {
        case MODELO_IMPUESTO.MODELO_111:
          return {
            elements: await getResumenImpuestos111(queryParams),
            tipo: MODELO_IMPUESTO.MODELO_111,
          }
        case MODELO_IMPUESTO.MODELO_115:
          return {
            elements: await getResumenImpuestos115(queryParams),
            tipo: MODELO_IMPUESTO.MODELO_115,
          }
        case MODELO_IMPUESTO.MODELO_303:
          return {
            elements: await getResumenImpuestos303(queryParams),
            tipo: MODELO_IMPUESTO.MODELO_303,
          }
        case MODELO_IMPUESTO.MODELO_349:
          return {
            elements: await getResumenImpuestos349(queryParams),
            tipo: MODELO_IMPUESTO.MODELO_349,
          }
        case MODELO_IMPUESTO.MODELO_390:
          return {
            elements: await getResumenImpuestos390(queryParams),
            tipo: MODELO_IMPUESTO.MODELO_390,
          }
        default:
          throw new Error("Tipo de documento no soportado")
      }
    },

    refetchOnWindowFocus: true,
    enabled: !!detailModel,
  })

  return (
    <div className='tw-px-4 tw-pb-4'>
      <div className='tw-rounded-t-xl tw-flex tw-bg-simple-10 tw-justify-between tw-items-center '>
        <p className='tw-py-5 tw-pl-6 tw-m-0 tw-text-lg tw-font-medium'>
          {t("taxSummary.tableSummaryTitle")}
        </p>
        <Dropdown
          id='rangosFechaResumenImpuesto'
          name='rangosFechaResumenImpuesto'
          className='tw-rounded-md tw-mr-6 tw-w-48'
          value={selectedDateRange}
          optionLabel='label'
          optionValue='value'
          options={Object.values(dateRangesList(t, selectedYear))}
          onChange={handleRangeDateSelection}
        />
      </div>
      {(data?.tipo === MODELO_IMPUESTO.MODELO_303 ||
        data?.tipo === MODELO_IMPUESTO.MODELO_390) && (
        <TableERP
          columns={RESUMEN_IMPUESTOS_VENTAS_COLUMNS(t)}
          className='tw-h-full'
          columnResizeMode='expand'
          value={data?.elements?.ventas ?? []}
          showGridlines
          dataKey='id'
          lazy
          scrollable
          size='small'
          loading={isLoading}
          emptyMessage={t("taxSummary.tableEmptyMessage")}
        />
      )}

      <TableERP
        columns={RESUMEN_IMPUESTOS_COMPRAS_COLUMNS(t)}
        className='tw-h-full'
        columnResizeMode='expand'
        value={data?.elements?.compras ?? []}
        showGridlines
        dataKey='id'
        lazy
        scrollable
        size='small'
        loading={isLoading}
        emptyMessage={t("taxSummary.tableEmptyMessage")}
      />
    </div>
  )
}
